import Utils from "../utils";

import EventContext from "./main/struct-events/event-context.js";
import EventCallbacks from "./main/struct-events/event-callbacks.js";
import StructCallbackWrapperContext from "./main/struct-events/struct-callback-wrapper-context.js";
import ShallowCallbackWrapperContext from "./main/struct-events/shallow-callback-wrapper-context.js";

import LivelabelManagerWrapper from "./livelabel-manager-wrapper.js";
import { deepAssignArray } from "./main/utils/deep-assign.js";

import ViewportContainer from "./viewport-container.js";
import Viewer from "./main/viewer.js";

let dataEventsPreviouslyInitialized = false;

let dataEventContexts = null;

let oldLivelabelManager = null;

let labelsData = null;
let sessionData = null;

let viewer = null;
let frameRequest = null;

function initDataEvents() {
    if (!Utils.queryParams.has("data_events")) {
        return;
    }

    const initializingStep = !dataEventsPreviouslyInitialized;
    if (initializingStep) {
        dataEventsPreviouslyInitialized = true;

        dataEventContexts = createDataEventContexts();
    }

    const { struct: { wrappers: wrapCtx, events: evtCtx } } = dataEventContexts;

    Q3.dataPlain = Q3.data;
    Q3.data = wrapCtx.getWrapper(Q3.data);

    Q3.dataEvents = evtCtx;

    if (Q3.labels.live !== oldLivelabelManager) {
        const manager = Q3.labels.live;
        oldLivelabelManager = manager;

        labelsData = wrapCtx.getWrapper(manager.serialize());
        Q3.labels.live = new LivelabelManagerWrapper(manager, () => {
            deepAssignArray(labelsData, manager.serialize());
        });
    }

    if (initializingStep) {
        sessionData = createSessionData(wrapCtx);
    }

    if (Utils.queryParams.has("data_events_viewer")) {
        if (initializingStep) {
            viewer = new DataEventsViewer(
                dataEventContexts,
                { urlPrefix: Q3.s3Url, domElement: document.body },
            );
        } else {
            viewer.stop();
        }

        viewer.setData(Q3.data, labelsData, sessionData);
        viewer.start();

        startUpdate();
    }
}

function startUpdate() {
    if (frameRequest) return;
    frameRequest = requestAnimationFrame(update);
}

function stopUpdate() {
    if (!frameRequest) return;
    cancelAnimationFrame(frameRequest);
    frameRequest = null;
}

function update() {
    frameRequest = requestAnimationFrame(update);

    if (sessionData) {
        const slideIndex = Q3.SlideManager.activeSlide;

        sessionData.slideIndex = slideIndex;
        sessionData.time = Date.now()*0.001;
    }

    if (viewer) {
        viewer.update();
    }
}

function createDataEventContexts() {
    const evtCtx = new EventContext();
    const evtCb = new EventCallbacks(evtCtx);
    const wrapCtx = new StructCallbackWrapperContext(evtCb);

    const shlEvtCtx = new EventContext();
    const shlEvtCb = new EventCallbacks(shlEvtCtx);
    const shlWrapCtx = new ShallowCallbackWrapperContext(shlEvtCb);

    return {
        struct: { wrappers: wrapCtx, events: evtCtx },
        shallow: { wrappers: shlWrapCtx, events: shlEvtCtx },
    };
}

function createSessionData(wrapCtx) {
    return wrapCtx.getWrapper({
        slideIndex: null,
        time: null,
        view: {
            size: {
                width: null,
                height: null,
            },
        },
    });
}

class DataEventsViewer {
    constructor(
        dataEventContexts,
        { urlPrefix, domElement },
    ) {
        this._running = false;

        this._viewportContainer = new ViewportContainer(domElement);

        const loadMan = new THREE.LoadingManager();
        loadMan.setURLModifier(url => urlPrefix+url);

        const fileArrayBufLoader = new THREE.FileLoader(loadMan);
        const gltfLoader = new THREE.GLTFLoader(loadMan);
        const texLoader = new THREE.TextureLoader(loadMan);
        fileArrayBufLoader.setResponseType("arraybuffer");

        this._tour = null;
        this._tourLabels = null;
        this._session = null;

        this._viewer = new Viewer(
            dataEventContexts,
            { fileArrayBufLoader, gltfLoader, texLoader, obj2dUrlPrefix: urlPrefix },
        );

        this._viewportContainer.viewport.appendChild(this._viewer.domElement);
    }

    setData(tour, tourLabels, session) {
        if (this._running) {
            this._viewer.stop();
        }

        this._tour = tour;
        this._tourLabels = tourLabels;
        this._session = session;

        if (this._running) {
            this._viewer.start(tour, tourLabels, session);
        }
    }

    start(tour, tourLabels, session) {
        if (this._running) {
            throw new Error("DataEventsViewer is already running");
        }

        this._running = true;

        const { width, height } = this._viewportContainer;
        this._viewer.setSize(width, height);

        this._viewer.start(this._tour, this._tourLabels, this._session);
    }

    stop() {
        if (!this._running) {
            throw new Error("DataEventsViewer is not running");
        }

        this._running = false;
        this._viewer.stop();
    }

    update() {
        const { width, height } = this._viewportContainer;

        this._viewer.setSize(width, height);

        if (this._session) {
            Object.assign(this._session.view.size, { width, height });
        }

        this._viewer.render();
    }
}

export default initDataEvents;
